import { logWarn } from "./logger";
// Lightweight IndexedDB wrapper for critical golf round data persistence
// Uses IndexedDB for reliability — survives refresh, crash, tab close

const DB_NAME = "ggr_golf";
const DB_VERSION = 1;
const STORES = {
  rounds: "active_rounds",
  sync: "sync_queue",
  cache: "data_cache",
};

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.rounds)) {
        db.createObjectStore(STORES.rounds, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.sync)) {
        db.createObjectStore(STORES.sync, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.cache)) {
        db.createObjectStore(STORES.cache, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });
  return dbPromise;
}

async function getStore(storeName, mode = "readonly") {
  const db = await openDB();
  const tx = db.transaction(storeName, mode);
  return tx.objectStore(storeName);
}

function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ── Active Round Storage ──

export async function saveActiveRound(roundData) {
  try {
    const store = await getStore(STORES.rounds, "readwrite");
    await promisify(store.put({ ...roundData, id: roundData.id || "current", savedAt: Date.now() }));
  } catch (e) {
    logWarn("IDB saveActiveRound failed, using localStorage:", e?.message);
    try { localStorage.setItem("ggr_active_round", JSON.stringify({ ...roundData, savedAt: Date.now() })); } catch (le) { logWarn("localStorage fallback failed:", le?.message); }
  }
}

export async function getActiveRound(id = "current") {
  try {
    const store = await getStore(STORES.rounds);
    const data = await promisify(store.get(id));
    return data || null;
  } catch (e) {
    logWarn("IDB getActiveRound failed, using localStorage:", e?.message);
    try {
      const raw = localStorage.getItem("ggr_active_round");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }
}

export async function deleteActiveRound(id = "current") {
  try {
    const store = await getStore(STORES.rounds, "readwrite");
    await promisify(store.delete(id));
  } catch (e) { logWarn("IDB deleteActiveRound:", e?.message); }
  try { localStorage.removeItem("ggr_active_round"); } catch (e) { logWarn("localStorage remove:", e?.message); }
}

export async function getAllActiveRounds() {
  try {
    const store = await getStore(STORES.rounds);
    return await promisify(store.getAll());
  } catch (e) { logWarn("IDB getAllActiveRounds:", e?.message); return []; }
}

// ── Sync Queue ──

export async function addToSyncQueue(item) {
  try {
    const store = await getStore(STORES.sync, "readwrite");
    await promisify(store.put({ ...item, id: item.id || `sync_${Date.now()}`, createdAt: Date.now(), retries: 0 }));
  } catch (e) { logWarn("IDB addToSyncQueue:", e?.message); }
}

export async function getSyncQueue() {
  try {
    const store = await getStore(STORES.sync);
    return await promisify(store.getAll());
  } catch (e) { logWarn("IDB getSyncQueue:", e?.message); return []; }
}

export async function removeSyncItem(id) {
  try {
    const store = await getStore(STORES.sync, "readwrite");
    await promisify(store.delete(id));
  } catch (e) { logWarn("IDB removeSyncItem:", e?.message); }
}

export async function updateSyncItem(item) {
  try {
    const store = await getStore(STORES.sync, "readwrite");
    await promisify(store.put(item));
  } catch (e) { logWarn("IDB updateSyncItem:", e?.message); }
}

// ── Data Cache (dashboard, bookings, etc.) ──

export async function setCacheData(key, data, ttlMs = 3600000) {
  const entry = { key, data, ts: Date.now(), ttl: ttlMs };
  try {
    const store = await getStore(STORES.cache, "readwrite");
    await promisify(store.put(entry));
  } catch (e) {
    logWarn("IDB setCacheData failed, using localStorage:", e?.message);
    try { localStorage.setItem(`ggr_cache_${key}`, JSON.stringify(entry)); } catch (le) { logWarn("localStorage cache fallback:", le?.message); }
  }
}

export async function getCacheData(key) {
  try {
    const store = await getStore(STORES.cache);
    const entry = await promisify(store.get(key));
    if (!entry) throw new Error("miss");
    if (Date.now() - entry.ts > entry.ttl) return { data: entry.data, stale: true, age: Date.now() - entry.ts };
    return { data: entry.data, stale: false, age: Date.now() - entry.ts };
  } catch {
    try {
      const raw = localStorage.getItem(`ggr_cache_${key}`);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      const stale = Date.now() - entry.ts > entry.ttl;
      return { data: entry.data, stale, age: Date.now() - entry.ts };
    } catch { return null; }
  }
}

export async function clearCacheData(key) {
  try {
    const store = await getStore(STORES.cache, "readwrite");
    if (key) {
      await promisify(store.delete(key));
    } else {
      await promisify(store.clear());
    }
  } catch (e) { logWarn("IDB clearCacheData:", e?.message); }
  if (key) {
    try { localStorage.removeItem(`ggr_cache_${key}`); } catch (e) { logWarn("localStorage clear:", e?.message); }
  }
}
